import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { blogPosts, blogCategories } from "@/mocks/blog";

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("tous");
  const [search, setSearch] = useState("");

  useEffect(() => {
    document.title = "Blog Diagnostic Immobilier Paris & IDF — Conseils & Réglementation | Up Diag";
    window.scrollTo(0, 0);
  }, []);

  const filtered = blogPosts.filter((p) => {
    const matchCat = activeCategory === "tous" || p.categorySlug === activeCategory;
    const matchSearch = search === "" || p.title.toLowerCase().includes(search.toLowerCase()) || p.excerpt.toLowerCase().includes(search.toLowerCase()) || p.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    return matchCat && matchSearch;
  });

  const featured = blogPosts[0];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      <Header />

      {/* Hero */}
      <section className="bg-[#0F3B5F] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/20 border border-[#F97316]/40 rounded-full text-[#F97316] text-sm font-semibold mb-5">
            <i className="ri-article-line"></i>
            Blog & Conseils
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
            Diagnostics immobiliers : conseils d'experts
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
            Réglementation, obligations, tarifs et guides locaux pour vendre ou louer votre bien à Paris et en Île-de-France.
          </p>
          {/* Search */}
          <div className="max-w-lg mx-auto relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-lg"></i>
            </div>
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-12 pl-11 pr-4 rounded-xl bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#F97316]"
            />
          </div>
        </div>
      </section>

      {/* Article vedette */}
      {activeCategory === "tous" && search === "" && (
        <section className="max-w-7xl mx-auto px-6 py-12">
          <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-gray-100 hover:border-[#F97316] transition-all duration-300 cursor-pointer">
            <div className="relative h-64 lg:h-auto overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute top-4 left-4 bg-[#F97316] text-white text-xs font-bold px-3 py-1.5 rounded-full">Article vedette</span>
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center bg-[#F8FAFC]">
              <span className="inline-block bg-orange-100 text-[#F97316] text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit">{featured.category}</span>
              <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-3 group-hover:text-[#F97316] transition-colors leading-snug" style={{ fontFamily: "Barlow, sans-serif" }}>
                {featured.title}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">{featured.excerpt}</p>
              <div className="flex items-center gap-4 text-xs text-gray-400 mb-5">
                <span><i className="ri-calendar-line mr-1"></i>{featured.date}</span>
                <span><i className="ri-time-line mr-1"></i>{featured.readTime} de lecture</span>
              </div>
              <span className="inline-flex items-center gap-2 text-[#F97316] font-semibold text-sm">
                Lire l'article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>
        </section>
      )}

      {/* Filtres catégories */}
      <section className="max-w-7xl mx-auto px-6 pb-4">
        <div className="flex flex-wrap gap-2">
          {blogCategories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.slug
                  ? "bg-[#0F3B5F] text-white"
                  : "bg-[#F8FAFC] text-[#334155] hover:bg-gray-100 border border-gray-100"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grille articles */}
      <section className="max-w-7xl mx-auto px-6 py-8 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-search-line text-4xl"></i>
            </div>
            <p className="text-base">Aucun article trouvé pour votre recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filtered.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 hover:border-[#F97316] transition-all duration-300 cursor-pointer block"
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <span className="absolute top-3 left-3 bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full">{post.category}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                    <span><i className="ri-calendar-line mr-1"></i>{post.date}</span>
                    <span><i className="ri-time-line mr-1"></i>{post.readTime}</span>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] text-base leading-snug mb-3 group-hover:text-[#F97316] transition-colors line-clamp-2" style={{ fontFamily: "Barlow, sans-serif" }}>
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">{tag}</span>
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-[#F97316] flex items-center gap-1">
                    Lire la suite <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
